/**
 * Izba Admin — Metrics Config
 *
 * UI for `/api/v1/admin/metrics-config`, data via DataHubSlice.
 */
import React, { useEffect, useMemo, useState } from "react";

import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleMinus,
  ClipboardCheck,
  Clock,
  Database,
  FlaskConical,
  History,
  ListOrdered,
  Loader2,
  PieChart,
  RefreshCw,
  RotateCcw,
  Save,
  SkipForward,
  XCircle,
} from "lucide-react";

import DefinitionsPanel from "./DefinitionsPanel";
import PipelineRunsPanel from "./PipelineRunsPanel";
import Button from "../../Components/Button";
import Select from "../../Components/Select";
import TabsComponent from "../../Components/Tab";
import Table from "../../Components/Table";
import ToggleSwitch from "../../Components/ToggleSwitch";
import { SyntheticNotice } from "../../data-hub-library/components/DatasetPill";
import {
  coerceDataset,
  DATASET_KINDS,
  DATASET_LABELS,
  DATASET_NOTICE,
  setActiveDataset,
  type DatasetKind,
} from "../../data-hub-library/data/dataset";
import type { DatasetAvailability } from "../../data-hub-library/data/types";
import { useActiveDataset } from "../../data-hub-library/hooks/useActiveDataset";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  clearDataHubQaRunResult,
  clearDataHubResetState,
  executeDataHubReset,
  fetchDataHubQaChecks,
  fetchDataHubQaRunDetail,
  fetchDataHubQaRunHistory,
  fetchDataHubWeightage,
  previewDataHubReset,
  runDataHubQa,
  updateDataHubQaCheck,
  updateDataHubWeightage,
  type DataHubQaRunCheckResult,
  type DataHubQaRunHistoryItem,
  type DataHubWeightCategory,
  type DataHubWeightMetric,
} from "../../Store/Reducers/DataHubSlice";
import { fetchAdminMetricsDatasets } from "../../Store/Reducers/MetricsSlice";

const BRAND_PURPLE = "#974ae4";

function AdminDatasetDropdown({
  value,
  availability,
  onChange,
}: {
  value: DatasetKind;
  availability?: DatasetAvailability[];
  onChange: (next: DatasetKind) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const summary = (kind: DatasetKind): string => {
    const row = availability?.find(a => a.dataset_kind === kind);
    if (!row) return "—";
    if (!row.has_data) return "no rows yet";
    const parts: string[] = [];
    if (row.staging) parts.push(`${row.staging.toLocaleString()} staging`);
    if (row.timeseries)
      parts.push(`${row.timeseries.toLocaleString()} timeseries`);
    if (row.runs) parts.push(`${row.runs.toLocaleString()} runs`);
    return parts.join(" · ") || "no rows yet";
  };

  return (
    <div className="relative min-w-[200px] w-[13.5rem]" ref={ref}>
      <div className="custom-input relative">
        <label htmlFor="metrics-config-dataset" className="custom-input__label">
          Dataset
        </label>
        <button
          id="metrics-config-dataset"
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
          className="custom-input__container custom-input__height flex w-full items-center justify-between gap-2 bg-white text-left text-sm text-gray-900"
        >
          <span className="truncate font-normal">{DATASET_LABELS[value]}</span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 z-50 mt-1.5 w-[18rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          <ul className="m-0 list-none p-1.5">
            {DATASET_KINDS.map(kind => {
              const active = kind === value;
              const synthetic = kind === "synthetic";
              return (
                <li key={kind}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => {
                      setOpen(false);
                      onChange(kind);
                    }}
                    className={[
                      "flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors",
                      active ? "bg-[#f5ebff]" : "hover:bg-gray-50",
                    ].join(" ")}
                  >
                    {synthetic ? (
                      <FlaskConical
                        className="mt-0.5 h-4 w-4 shrink-0"
                        style={{ color: BRAND_PURPLE }}
                      />
                    ) : (
                      <Database className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span
                        className="block text-sm font-medium"
                        style={{ color: active ? BRAND_PURPLE : "#111827" }}
                      >
                        {DATASET_LABELS[kind]}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {summary(kind)}
                      </span>
                    </span>
                    {active && (
                      <Check
                        className="mt-0.5 h-4 w-4 shrink-0"
                        style={{ color: BRAND_PURPLE }}
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="m-0 border-t border-gray-100 px-3 py-2 text-[11px] leading-snug text-gray-500">
            {DATASET_NOTICE}
          </p>
        </div>
      )}
    </div>
  );
}

export type OrganizationFromAdmin = {
  tenant_id?: string;
  organization: string;
  domain?: string;
  tenant_schema_name?: string;
  users_count?: number;
  status?: string;
};

type MetricsSubTab =
  | "weightage"
  | "definitions"
  | "qa"
  | "reset"
  | "pipeline-runs";

const SUB_TABS: { id: MetricsSubTab; label: string; icon: React.ReactNode }[] =
  [
    { id: "weightage", label: "Weightage", icon: <PieChart size={16} /> },
    { id: "definitions", label: "Definitions", icon: <Database size={16} /> },
    { id: "qa", label: "QA management", icon: <ClipboardCheck size={16} /> },
    { id: "reset", label: "Org reset", icon: <RotateCcw size={16} /> },
    {
      id: "pipeline-runs",
      label: "Pipeline runs",
      icon: <ListOrdered size={16} />,
    },
  ];

const ORG_SCOPED: MetricsSubTab[] = ["qa", "reset", "pipeline-runs"];

interface Props {
  organizations: OrganizationFromAdmin[];
  loading?: boolean;
}

const AdminMetricsConfigContainer: React.FC<Props> = ({
  organizations,
  loading: orgsLoading,
}) => {
  const dispatch = useAppDispatch();
  const [subTab, setSubTab] = useState<MetricsSubTab>("weightage");
  const [orgId, setOrgId] = useState("");
  const [dataset] = useActiveDataset();
  const [datasetKey, setDatasetKey] = useState(0);
  const [availability, setAvailability] = useState<
    DatasetAvailability[] | undefined
  >();

  const selectedOrg = useMemo(
    () => organizations.find(o => o.tenant_id === orgId) || null,
    [organizations, orgId]
  );

  const needsOrg = ORG_SCOPED.includes(subTab);

  useEffect(() => {
    if (orgId) return;
    const first =
      organizations.find(o => o.status === "active" && o.tenant_id) ||
      organizations.find(o => o.tenant_id);
    if (first?.tenant_id) setOrgId(first.tenant_id);
  }, [organizations, orgId]);

  useEffect(() => {
    if (!orgId || !needsOrg) {
      setAvailability(undefined);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const content = await dispatch(
          fetchAdminMetricsDatasets({ organization_id: orgId })
        ).unwrap();
        const rows = Array.isArray(content?.datasets) ? content.datasets : [];
        if (!cancelled) {
          setAvailability(
            rows.map((r: any) => ({
              dataset_kind: coerceDataset(r?.dataset_kind),
              staging: Number(r?.staging ?? 0),
              timeseries: Number(r?.timeseries ?? 0),
              runs: Number(r?.runs ?? 0),
              has_data: Boolean(r?.has_data),
            }))
          );
        }
      } catch {
        if (!cancelled) setAvailability(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, needsOrg, datasetKey, dispatch]);

  const onDatasetChange = (next: DatasetKind) => {
    setActiveDataset(next);
    setDatasetKey(k => k + 1);
  };

  return (
    <div className="admin-page__content space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="border-b border-gray-200">
        <TabsComponent
          tabs={SUB_TABS}
          activeTab={subTab}
          onTabChange={id => setSubTab(id as MetricsSubTab)}
          variant="tab-underline"
          className="mb-0 border-b"
        />
      </div>

      {needsOrg && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
            <Select
              className="min-w-[240px] w-[min(100%,28rem)]"
              label="Organization"
              name="metrics-config-org"
              value={orgId}
              disabled={orgsLoading || !organizations.length}
              placeholder={
                organizations.length
                  ? "Select organization…"
                  : "No organizations"
              }
              isShowPlaceHolder={!orgId || !organizations.length}
              options={organizations
                .filter(org => org.tenant_id)
                .map(org => ({
                  value: org.tenant_id as string,
                  label: org.domain
                    ? `${org.organization} · ${org.domain}`
                    : org.organization,
                }))}
              onChange={e => setOrgId(e.target.value)}
            />
            <AdminDatasetDropdown
              value={dataset}
              availability={availability}
              onChange={onDatasetChange}
            />
          </div>
          <SyntheticNotice dataset={dataset} />
        </div>
      )}

      {subTab === "weightage" && <WeightagePanel />}
      {subTab === "definitions" && <DefinitionsPanel />}
      {subTab === "qa" &&
        (orgId ? (
          <QaPanel
            orgId={orgId}
            orgName={selectedOrg?.organization || ""}
            datasetKey={datasetKey}
            dataset={dataset}
          />
        ) : (
          <p className="text-sm text-gray-500">Select an organization.</p>
        ))}
      {subTab === "reset" &&
        (orgId ? (
          <ResetPanel
            orgId={orgId}
            orgName={selectedOrg?.organization || ""}
            datasetKey={datasetKey}
            dataset={dataset}
          />
        ) : (
          <p className="text-sm text-gray-500">Select an organization.</p>
        ))}
      {subTab === "pipeline-runs" &&
        (orgId ? (
          <PipelineRunsPanel
            orgId={orgId}
            orgName={selectedOrg?.organization || ""}
            datasetKey={datasetKey}
            dataset={dataset}
          />
        ) : (
          <p className="text-sm text-gray-500">Select an organization.</p>
        ))}
    </div>
  );
};

function cloneCategories(
  cats: DataHubWeightCategory[] | undefined
): DataHubWeightCategory[] {
  return (cats || []).map(c => ({
    ...c,
    weight: Number(c.weight) || 0,
    metrics: (c.metrics || []).map(m => ({
      ...m,
      weight: Number(m.weight) || 0,
    })),
  }));
}

function formatWeightLabel(name: string): string {
  if (!name) return "";
  if (/\s/.test(name) || /[A-Z]/.test(name.slice(1))) return name;
  return name.replace(/[_-]+/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function WeightagePanel() {
  const dispatch = useAppDispatch();
  const {
    data: versions,
    loading,
    error,
    saving,
  } = useAppSelector(s => s.dataHub.weightage);

  const [categories, setCategories] = useState<DataHubWeightCategory[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const defaultConfig = versions.find(v => v.is_active) || versions[0] || null;
  const title =
    defaultConfig?.label?.trim() ||
    (defaultConfig
      ? `Platform default v${defaultConfig.version}`
      : "Platform default");

  useEffect(() => {
    void dispatch(fetchDataHubWeightage());
  }, [dispatch]);

  useEffect(() => {
    const cfg = versions.find(v => v.is_active) || versions[0] || null;
    if (!cfg) {
      setCategories([]);
      return;
    }
    const next = cloneCategories(cfg.config?.categories);
    setCategories(next);
    if (next.length) {
      setExpanded(prev => (prev.size ? prev : new Set([next[0].name])));
    }
  }, [versions]);

  const categoryTotal = categories.reduce((sum, c) => {
    if (c.enabled === false) return sum;
    return sum + (Number(c.weight) || 0);
  }, 0);
  const categoryTotalOk = categoryTotal === 100;

  const metricTotals = categories.map(cat => {
    const enabledMetrics = (cat.metrics || []).filter(m => m.enabled !== false);
    const total = enabledMetrics.reduce(
      (sum, m) => sum + (Number(m.weight) || 0),
      0
    );
    return {
      total,
      hasEnabled: enabledMetrics.length > 0,
      ok: enabledMetrics.length === 0 || total === 100,
    };
  });
  const metricWeightsOk = metricTotals.every(t => t.ok);
  const weightsValid = categoryTotalOk && metricWeightsOk;

  const updateCategory = (
    index: number,
    patch: Partial<DataHubWeightCategory>
  ) => {
    setCategories(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const updateMetric = (
    categoryIndex: number,
    metricIndex: number,
    patch: Partial<NonNullable<DataHubWeightCategory["metrics"]>[number]>
  ) => {
    setCategories(prev => {
      const next = [...prev];
      const cat = next[categoryIndex];
      const metrics = [...(cat.metrics || [])];
      metrics[metricIndex] = { ...metrics[metricIndex], ...patch };
      next[categoryIndex] = { ...cat, metrics };
      return next;
    });
  };

  const toggleExpanded = (name: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const saveDefault = () => {
    if (!defaultConfig?.id || !weightsValid) return;
    void dispatch(
      updateDataHubWeightage({
        configId: defaultConfig.id,
        label: defaultConfig.label || title,
        config: { categories },
      })
    );
  };

  return (
    <div className="space-y-5">
      {error && <ErrorBanner text={error} />}

      {loading && !categories.length ? (
        <LoadingRow text="Loading weightage…" />
      ) : !categories.length ? (
        <p className="text-sm text-gray-500">No category weights loaded.</p>
      ) : (
        <>
          <section className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h4 className="m-0 text-lg font-semibold text-gray-900">
                  Category Weights
                </h4>
                <p className="mb-0 mt-1 text-sm text-gray-600">
                  These control how much each category contributes to the
                  overall KPI score.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={loading || saving}
                  onClick={() => void dispatch(fetchDataHubWeightage())}
                  icon={
                    <RefreshCw
                      className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                    />
                  }
                >
                  Refresh
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  disabled={
                    saving ||
                    !defaultConfig ||
                    !categories.length ||
                    !weightsValid
                  }
                  loading={saving}
                  onClick={saveDefault}
                  title={
                    !weightsValid
                      ? "Category and metric weights must each total 100%"
                      : undefined
                  }
                  icon={<Save className="h-4 w-4" />}
                >
                  Save
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {categories.map((cat, ci) => {
                const isActive = cat.enabled !== false;
                const weight = Math.max(
                  0,
                  Math.min(100, Number(cat.weight) || 0)
                );
                const displayWeight = isActive ? weight : 0;
                return (
                  <div
                    key={`${cat.name}-${ci}`}
                    className={`rounded-lg border border-gray-200 bg-gray-50 p-3 ${
                      isActive ? "" : "opacity-70"
                    }`}
                  >
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-gray-700">
                        {formatWeightLabel(cat.name)}
                      </div>
                      <ToggleSwitch
                        id={`weight-cat-${cat.name}`}
                        checked={isActive}
                        disabled={saving}
                        onChange={next => updateCategory(ci, { enabled: next })}
                      />
                    </div>
                    <div className="mb-2 h-1 w-full rounded bg-gray-200">
                      <div
                        className="h-full rounded bg-violet-500"
                        style={{ width: `${displayWeight}%` }}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={displayWeight}
                        disabled={!isActive || saving}
                        onChange={e =>
                          updateCategory(ci, {
                            weight: Number(e.target.value) || 0,
                          })
                        }
                        className="w-14 rounded border border-gray-200 bg-white px-2 py-1 text-sm font-medium text-violet-600 disabled:cursor-not-allowed disabled:bg-gray-100"
                      />
                      <span className="text-sm text-gray-500">%</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-1 text-sm text-gray-700">
              <span className="font-medium">Category weights total:</span>
              <span
                className={
                  categoryTotalOk ? "text-green-700" : "text-amber-700"
                }
              >
                {categoryTotal}%{categoryTotalOk ? " ✓" : ""}
              </span>
            </div>
            {!categoryTotalOk && (
              <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                Category weights must total exactly 100% (currently{" "}
                {categoryTotal}%). Disabled categories count as 0.
              </div>
            )}
          </section>

          <section className="overflow-hidden rounded-xl border border-gray-200/70 bg-white shadow-sm">
            <div className="border-b border-gray-100 bg-gray-50/70 px-4 py-3">
              <div className="flex flex-wrap items-center gap-3">
                <h4 className="m-0 text-lg font-semibold text-gray-900">
                  Metric Configuration
                </h4>
                <p className="m-0 text-sm text-gray-500">
                  Click a category to expand. Toggle metrics on/off and adjust
                  weights within each category.
                </p>
              </div>
            </div>

            {!metricWeightsOk && (
              <div className="mx-4 mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                Enabled metric weights must total exactly 100% within each
                category before saving.
              </div>
            )}

            <div className="overflow-hidden rounded-b-xl">
              <Table
                columns={[
                  {
                    key: "name",
                    title: "Category",
                    render: (_: unknown, cat: DataHubWeightCategory) => {
                      const isOpen = expanded.has(cat.name);
                      return (
                        <Button
                          type="button"
                          variant="clear"
                          className="!justify-start px-0 py-0 font-semibold text-gray-800"
                          onClick={() => toggleExpanded(cat.name)}
                          icon={
                            isOpen ? (
                              <ChevronDown className="h-4 w-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-500" />
                            )
                          }
                        >
                          {formatWeightLabel(cat.name)}
                        </Button>
                      );
                    },
                  },
                  {
                    key: "metric_total",
                    title: "Metric weights",
                    width: "140px",
                    textalign: "right" as const,
                    render: (_: unknown, cat: DataHubWeightCategory) => {
                      const ci = categories.findIndex(c => c.name === cat.name);
                      const info = metricTotals[ci];
                      if (!info?.hasEnabled) return null;
                      return (
                        <span
                          className={`text-xs font-medium ${
                            info.ok ? "text-green-700" : "text-amber-700"
                          }`}
                        >
                          {info.total}%{info.ok ? " ✓" : ""}
                        </span>
                      );
                    },
                  },
                ]}
                data={categories}
                rowKey={(cat: DataHubWeightCategory) => cat.name}
                expandedRowKeys={Array.from(expanded)}
                expandedRowRender={(cat: DataHubWeightCategory) => {
                  const ci = categories.findIndex(c => c.name === cat.name);
                  const metrics = cat.metrics || [];
                  const metricTotalInfo = metricTotals[ci];
                  return (
                    <div className="space-y-2">
                      <Table
                        columns={[
                          {
                            key: "name",
                            title: "Metric",
                            render: (_: unknown, m: DataHubWeightMetric) => (
                              <div className="min-w-0">
                                <p className="m-0 truncate font-medium text-gray-800">
                                  {m.heading || formatWeightLabel(m.name)}
                                </p>
                                {m.description ? (
                                  <p className="m-0 truncate pt-0.5 text-xs text-gray-500">
                                    {m.description}
                                  </p>
                                ) : null}
                              </div>
                            ),
                          },
                          {
                            key: "enabled",
                            title: "Include",
                            width: "100px",
                            textalign: "center" as const,
                            render: (_: unknown, m: DataHubWeightMetric) => {
                              const mi = metrics.findIndex(
                                x => x.name === m.name
                              );
                              return (
                                <div className="flex justify-center">
                                  <ToggleSwitch
                                    id={`weight-metric-${cat.name}-${m.name}`}
                                    checked={m.enabled !== false}
                                    disabled={saving}
                                    onChange={next =>
                                      updateMetric(ci, mi, { enabled: next })
                                    }
                                  />
                                </div>
                              );
                            },
                          },
                          {
                            key: "weight",
                            title: "Weight",
                            width: "110px",
                            textalign: "center" as const,
                            render: (_: unknown, m: DataHubWeightMetric) => {
                              const mi = metrics.findIndex(
                                x => x.name === m.name
                              );
                              const included = m.enabled !== false;
                              return (
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  step={1}
                                  value={included ? Number(m.weight) || 0 : 0}
                                  disabled={!included || saving}
                                  onChange={e =>
                                    updateMetric(ci, mi, {
                                      weight: Number(e.target.value) || 0,
                                    })
                                  }
                                  className="w-14 rounded border border-gray-200 bg-white px-2 py-1 text-center text-sm font-medium text-violet-600 disabled:cursor-not-allowed disabled:bg-gray-100"
                                />
                              );
                            },
                          },
                        ]}
                        data={metrics}
                        rowKey={(m: DataHubWeightMetric, i: number) =>
                          `${m.name}-${i}`
                        }
                        emptyContent={
                          <p className="m-0 text-sm text-gray-500">
                            No metrics in this category.
                          </p>
                        }
                      />
                      {metrics.length > 0 && (
                        <div className="text-xs">
                          <p className="mb-1 text-gray-500">
                            Enabled metric weights must total 100% within this
                            category. Disabled metrics count as 0.
                          </p>
                          <span
                            className={`font-medium ${
                              metricTotalInfo.ok
                                ? "text-green-700"
                                : "text-amber-700"
                            }`}
                          >
                            Total: {metricTotalInfo.total}%
                            {metricTotalInfo.ok ? " ✓" : ""}
                          </span>
                          {metricTotalInfo.hasEnabled &&
                            !metricTotalInfo.ok && (
                              <p className="mt-1 mb-0 text-red-700">
                                Metric weights in this category must total
                                exactly 100% (currently {metricTotalInfo.total}
                                %).
                              </p>
                            )}
                        </div>
                      )}
                    </div>
                  );
                }}
              />
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function QaPanel({
  orgId,
  orgName,
  datasetKey,
  dataset,
}: {
  orgId: string;
  orgName: string;
  datasetKey: number;
  dataset: DatasetKind;
}) {
  const dispatch = useAppDispatch();
  const {
    data: checks,
    loading,
    error,
    running,
    runResult,
    selectedRunId,
    history,
    historyLoading,
  } = useAppSelector(s => s.dataHub.qa);
  const [checksOpen, setChecksOpen] = useState(true);
  const [resultFilter, setResultFilter] = useState<QaResultFilter>("all");
  const resultsRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void dispatch(fetchDataHubQaChecks(orgId));
    void dispatch(fetchDataHubQaRunHistory(orgId));
    dispatch(clearDataHubQaRunResult());
    setResultFilter("all");
    setChecksOpen(true);
  }, [dispatch, orgId, datasetKey]);

  useEffect(() => {
    if (!runResult?.checks || running) return;
    setChecksOpen(false);
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [runResult, running]);

  const enabledCount = checks.filter(c => c.enabled).length;
  const summary = runResult?.summary;
  const resultChecks = runResult?.checks || [];
  const filteredResults = useMemo(
    () => filterQaResults(resultChecks, resultFilter),
    [resultChecks, resultFilter]
  );

  const issueCount =
    Number(summary?.failures || 0) +
    Number(summary?.errors || 0) +
    Number(summary?.warnings || 0);
  const allClear =
    !!summary &&
    issueCount === 0 &&
    Number(summary.total || resultChecks.length) > 0;

  const onRun = () => {
    setResultFilter("all");
    void dispatch(runDataHubQa(orgId));
  };

  const onSelectRun = (runId: string) => {
    setResultFilter("all");
    void dispatch(fetchDataHubQaRunDetail({ organizationId: orgId, runId }));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="m-0 text-base font-semibold text-gray-900">
            QA management — {orgName}
          </h3>
          <p className="m-0 text-xs text-gray-500">
            Configure checks, run the suite, then review history and results for
            this org&apos;s <strong>{DATASET_LABELS[dataset]}</strong> dataset.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline-primary"
            disabled={loading || running || historyLoading}
            onClick={() => {
              void dispatch(fetchDataHubQaChecks(orgId));
              void dispatch(fetchDataHubQaRunHistory(orgId));
            }}
            icon={
              <RefreshCw
                className={`h-3.5 w-3.5 ${loading || historyLoading ? "animate-spin" : ""}`}
              />
            }
          >
            Refresh
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={running || !enabledCount}
            loading={running}
            onClick={onRun}
            icon={<ClipboardCheck className="h-3.5 w-3.5" />}
          >
            {running ? "Running…" : "Run QA"}
          </Button>
        </div>
      </div>

      {error && <ErrorBanner text={error} />}

      <section className="overflow-hidden rounded-md border border-gray-200">
        <Button
          type="button"
          variant="clear"
          className="!w-full !justify-between rounded-none bg-gray-50 px-3 py-2 text-left"
          onClick={() => setChecksOpen(open => !open)}
          icon={
            checksOpen ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )
          }
        >
          <span className="flex flex-1 items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">
                Check configuration
              </span>
              <span className="rounded bg-white px-1.5 py-0.5 text-[11px] text-gray-600 ring-1 ring-gray-200">
                {enabledCount}/{checks.length} enabled
              </span>
            </span>
            <span className="text-[11px] text-gray-500">
              {checksOpen ? "Hide" : "Show"} checks
            </span>
          </span>
        </Button>
        {checksOpen && (
          <div className="border-t border-gray-200 p-3">
            {loading && !checks.length ? (
              <LoadingRow text="Loading QA checks…" />
            ) : (
              <div className="max-h-[280px] space-y-2 overflow-y-auto">
                {checks.map(c => (
                  <label
                    key={c.check_key}
                    className="flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 px-3 py-2 hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={!!c.enabled}
                      onChange={() =>
                        void dispatch(
                          updateDataHubQaCheck({
                            organizationId: orgId,
                            checkKey: c.check_key,
                            enabled: !c.enabled,
                          })
                        )
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="m-0 text-sm font-medium text-gray-900">
                          {c.label}
                        </p>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${
                            c.severity === "fail"
                              ? "bg-red-50 text-red-700"
                              : c.severity === "warn"
                                ? "bg-amber-50 text-amber-800"
                                : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {c.severity}
                        </span>
                        {c.group_name && (
                          <span className="text-[10px] uppercase text-gray-400">
                            {c.group_name}
                          </span>
                        )}
                      </div>
                      {c.description && (
                        <p className="m-0 text-xs text-gray-500">
                          {c.description}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
                {!checks.length && (
                  <p className="text-sm text-gray-500">
                    No QA checks for this org.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <div
        ref={resultsRef}
        className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]"
      >
        <section className="overflow-hidden rounded-md border border-gray-200">
          <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-gray-500" />
              <div>
                <p className="m-0 text-sm font-semibold text-gray-900">
                  Run history
                </p>
                <p className="m-0 text-[11px] text-gray-500">
                  {history.length
                    ? `${history.length} recent run${history.length === 1 ? "" : "s"}`
                    : "Pipeline of past QA runs"}
                </p>
              </div>
            </div>
          </div>
          <div className="max-h-[560px] overflow-y-auto p-2">
            {historyLoading && !history.length ? (
              <LoadingRow text="Loading history…" />
            ) : !history.length ? (
              <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-6 text-center">
                <p className="m-0 text-xs text-gray-500">
                  No runs yet. Click Run QA to start the pipeline.
                </p>
              </div>
            ) : (
              <ol className="m-0 space-y-0 p-0">
                {history.map((run, index) => (
                  <QaHistoryItem
                    key={run.id}
                    run={run}
                    isFirst={index === 0}
                    isLast={index === history.length - 1}
                    selected={selectedRunId === run.id}
                    onSelect={() => onSelectRun(run.id)}
                  />
                ))}
              </ol>
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-md border border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 py-2.5">
            <div>
              <p className="m-0 text-sm font-semibold text-gray-900">
                Test results
              </p>
              <p className="m-0 text-[11px] text-gray-500">
                {running
                  ? "Suite is running against this organization’s KPI tables…"
                  : runResult
                    ? [
                        runResult.id ? `Run ${runResult.id.slice(0, 8)}` : null,
                        formatQaDuration(runResult.duration_ms),
                        formatQaWhen(
                          runResult.started_at || runResult.created_at
                        ),
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : "Select a history run or click Run QA"}
              </p>
            </div>
            {runResult && !running && (
              <Button
                type="button"
                variant="clear"
                className="!px-0 text-xs text-gray-500"
                onClick={() => {
                  dispatch(clearDataHubQaRunResult());
                  void dispatch(fetchDataHubQaRunHistory(orgId));
                }}
              >
                Clear selection
              </Button>
            )}
          </div>

          <div className="space-y-3 p-3">
            {running && (
              <div className="flex items-center gap-2 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-3 text-sm text-indigo-900">
                <Loader2 className="h-4 w-4 animate-spin" />
                Running {enabledCount} enabled check
                {enabledCount === 1 ? "" : "s"}…
              </div>
            )}

            {!running && !runResult && (
              <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center">
                <ClipboardCheck className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                <p className="m-0 text-sm font-medium text-gray-700">
                  No run selected
                </p>
                <p className="m-0 mt-1 text-xs text-gray-500">
                  Pick a run from history, or run QA to create a new result.
                </p>
              </div>
            )}

            {!running && runResult && (
              <>
                {summary && (
                  <div
                    className={`rounded-md border px-3 py-2 text-sm ${
                      allClear
                        ? "border-green-200 bg-green-50 text-green-900"
                        : Number(summary.failures || 0) +
                              Number(summary.errors || 0) >
                            0
                          ? "border-red-200 bg-red-50 text-red-900"
                          : "border-amber-200 bg-amber-50 text-amber-950"
                    }`}
                  >
                    {allClear
                      ? "All enabled checks passed."
                      : Number(summary.failures || 0) +
                            Number(summary.errors || 0) >
                          0
                        ? `${Number(summary.failures || 0) + Number(summary.errors || 0)} check(s) need attention.`
                        : `${Number(summary.warnings || 0)} warning(s) found — review below.`}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                  <QaSummaryTile
                    label="Total"
                    value={Number(summary?.total || resultChecks.length)}
                    tone="neutral"
                  />
                  <QaSummaryTile
                    label="Passed"
                    value={Number(summary?.passed || 0)}
                    tone="pass"
                  />
                  <QaSummaryTile
                    label="Warnings"
                    value={Number(summary?.warnings || 0)}
                    tone="warn"
                  />
                  <QaSummaryTile
                    label="Failures"
                    value={Number(summary?.failures || 0)}
                    tone="fail"
                  />
                  <QaSummaryTile
                    label="Errors"
                    value={Number(summary?.errors || 0)}
                    tone="error"
                  />
                  <QaSummaryTile
                    label="Skipped"
                    value={Number(summary?.skipped || 0)}
                    tone="skip"
                  />
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {(
                    [
                      ["all", "All", resultChecks.length],
                      [
                        "issues",
                        "Issues",
                        resultChecks.filter(c =>
                          ["fail", "error", "warn"].includes(
                            String(c.status || "").toLowerCase()
                          )
                        ).length,
                      ],
                      [
                        "fail",
                        "Fail",
                        resultChecks.filter(
                          c => String(c.status).toLowerCase() === "fail"
                        ).length,
                      ],
                      [
                        "warn",
                        "Warn",
                        resultChecks.filter(
                          c => String(c.status).toLowerCase() === "warn"
                        ).length,
                      ],
                      [
                        "pass",
                        "Pass",
                        resultChecks.filter(
                          c => String(c.status).toLowerCase() === "pass"
                        ).length,
                      ],
                      [
                        "skipped",
                        "Skipped",
                        resultChecks.filter(
                          c => String(c.status).toLowerCase() === "skipped"
                        ).length,
                      ],
                      [
                        "error",
                        "Error",
                        resultChecks.filter(
                          c => String(c.status).toLowerCase() === "error"
                        ).length,
                      ],
                    ] as Array<[QaResultFilter, string, number]>
                  ).map(([id, label, count]) => (
                    <Button
                      key={id}
                      type="button"
                      variant={resultFilter === id ? "primary" : "gray"}
                      className="!rounded-full !px-2.5 !py-1 !text-[11px]"
                      onClick={() => setResultFilter(id)}
                    >
                      {label} ({count})
                    </Button>
                  ))}
                </div>

                <div className="max-h-[420px] space-y-2 overflow-y-auto">
                  {filteredResults.map(c => (
                    <QaResultRow key={c.id || c.label} check={c} />
                  ))}
                  {!filteredResults.length && (
                    <p className="rounded-md border border-dashed border-gray-200 px-3 py-6 text-center text-xs text-gray-500">
                      No checks match this filter.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function QaHistoryItem({
  run,
  selected,
  isFirst,
  isLast,
  onSelect,
}: {
  run: DataHubQaRunHistoryItem;
  selected: boolean;
  isFirst: boolean;
  isLast: boolean;
  onSelect: () => void;
}) {
  const status = String(run.status || "").toLowerCase();
  const summary = run.summary || {};
  return (
    <li className="relative flex gap-3 pb-3 last:pb-0">
      <div className="flex w-4 flex-col items-center">
        {!isFirst && <span className="h-1.5 w-px bg-gray-200" />}
        <span
          className={`mt-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white ${
            status === "passed"
              ? "bg-green-500"
              : status === "failed"
                ? "bg-red-500"
                : status === "warn"
                  ? "bg-amber-400"
                  : "bg-gray-300"
          }`}
        />
        {!isLast && <span className="mt-1 w-px flex-1 bg-gray-200" />}
      </div>
      <button
        type="button"
        onClick={onSelect}
        className={`min-w-0 flex-1 rounded-md border px-2.5 py-2 text-left transition ${
          selected
            ? "border-indigo-300 bg-indigo-50 shadow-sm"
            : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <p className="m-0 text-xs font-semibold text-gray-900">
            Run #{run.run_no ?? "—"}
          </p>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
              status === "passed"
                ? "bg-green-100 text-green-800"
                : status === "failed"
                  ? "bg-red-100 text-red-800"
                  : status === "warn"
                    ? "bg-amber-100 text-amber-900"
                    : "bg-gray-100 text-gray-600"
            }`}
          >
            {status || "unknown"}
          </span>
        </div>
        <p className="m-0 mt-1 flex items-center gap-1 text-[11px] text-gray-500">
          <Clock className="h-3 w-3" />
          {formatQaWhen(run.started_at || run.created_at)}
        </p>
        <p className="m-0 mt-0.5 text-[11px] text-gray-500">
          {formatQaDuration(run.duration_ms) || "Duration n/a"}
          {" · "}
          {Number(summary.passed || 0)} pass
          {Number(summary.warnings || 0) > 0
            ? ` · ${Number(summary.warnings)} warn`
            : ""}
          {Number(summary.failures || 0) + Number(summary.errors || 0) > 0
            ? ` · ${Number(summary.failures || 0) + Number(summary.errors || 0)} fail`
            : ""}
        </p>
      </button>
    </li>
  );
}

function formatQaDuration(ms?: number | null): string | null {
  if (ms == null || Number.isNaN(Number(ms))) return null;
  const value = Number(ms);
  if (value < 1000) return `${value} ms`;
  if (value < 60_000) return `${(value / 1000).toFixed(1)} s`;
  const minutes = Math.floor(value / 60_000);
  const seconds = Math.round((value % 60_000) / 1000);
  return `${minutes}m ${seconds}s`;
}

function formatQaWhen(value?: string | null): string {
  if (!value) return "Unknown time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type QaResultFilter =
  | "all"
  | "issues"
  | "pass"
  | "warn"
  | "fail"
  | "error"
  | "skipped";

function filterQaResults(
  checks: DataHubQaRunCheckResult[],
  filter: QaResultFilter
): DataHubQaRunCheckResult[] {
  if (filter === "all") return checks;
  if (filter === "issues") {
    return checks.filter(c =>
      ["fail", "error", "warn"].includes(String(c.status || "").toLowerCase())
    );
  }
  return checks.filter(c => String(c.status || "").toLowerCase() === filter);
}

function QaSummaryTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "neutral" | "pass" | "warn" | "fail" | "error" | "skip";
}) {
  const toneClass =
    tone === "pass"
      ? "border-green-200 bg-green-50 text-green-900"
      : tone === "warn"
        ? "border-amber-200 bg-amber-50 text-amber-950"
        : tone === "fail" || tone === "error"
          ? "border-red-200 bg-red-50 text-red-900"
          : tone === "skip"
            ? "border-gray-200 bg-gray-50 text-gray-700"
            : "border-gray-200 bg-white text-gray-800";
  return (
    <div className={`rounded-md border px-2.5 py-2 ${toneClass}`}>
      <p className="m-0 text-[10px] font-semibold uppercase tracking-wide opacity-70">
        {label}
      </p>
      <p className="m-0 text-lg font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function QaResultRow({ check }: { check: DataHubQaRunCheckResult }) {
  const status = String(check.status || "").toLowerCase();
  const meta = qaStatusMeta(status);
  return (
    <div
      className={`flex gap-3 rounded-md border border-l-4 bg-white px-3 py-2.5 ${meta.border} ${meta.left}`}
    >
      <div className={`mt-0.5 shrink-0 ${meta.iconClass}`}>{meta.icon}</div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="m-0 text-sm font-medium text-gray-900">
            {check.label || check.id}
          </p>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${meta.badge}`}
          >
            {status || "unknown"}
          </span>
          {check.group && (
            <span className="text-[10px] uppercase tracking-wide text-gray-400">
              {check.group}
            </span>
          )}
          {check.count != null && Number(check.count) > 0 && (
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] tabular-nums text-gray-600">
              {Number(check.count).toLocaleString()} rows
            </span>
          )}
        </div>
        {check.detail ? (
          <p className="m-0 mt-1 text-xs leading-relaxed text-gray-600">
            {check.detail}
          </p>
        ) : status === "pass" ? (
          <p className="m-0 mt-1 text-xs text-gray-400">No issues found.</p>
        ) : null}
      </div>
    </div>
  );
}

function qaStatusMeta(status: string) {
  switch (status) {
    case "pass":
      return {
        icon: <CheckCircle2 className="h-4 w-4" />,
        iconClass: "text-green-600",
        badge: "bg-green-100 text-green-800",
        border: "border-gray-200",
        left: "border-l-green-500",
      };
    case "warn":
      return {
        icon: <AlertTriangle className="h-4 w-4" />,
        iconClass: "text-amber-500",
        badge: "bg-amber-100 text-amber-900",
        border: "border-gray-200",
        left: "border-l-amber-400",
      };
    case "fail":
      return {
        icon: <XCircle className="h-4 w-4" />,
        iconClass: "text-red-600",
        badge: "bg-red-100 text-red-800",
        border: "border-gray-200",
        left: "border-l-red-500",
      };
    case "error":
      return {
        icon: <CircleMinus className="h-4 w-4" />,
        iconClass: "text-red-700",
        badge: "bg-red-100 text-red-900",
        border: "border-gray-200",
        left: "border-l-red-700",
      };
    case "skipped":
      return {
        icon: <SkipForward className="h-4 w-4" />,
        iconClass: "text-gray-400",
        badge: "bg-gray-100 text-gray-600",
        border: "border-gray-200",
        left: "border-l-gray-300",
      };
    default:
      return {
        icon: <AlertTriangle className="h-4 w-4" />,
        iconClass: "text-gray-500",
        badge: "bg-gray-100 text-gray-700",
        border: "border-gray-200",
        left: "border-l-gray-400",
      };
  }
}

function ResetPanel({
  orgId,
  orgName,
  datasetKey,
  dataset,
}: {
  orgId: string;
  orgName: string;
  datasetKey: number;
  dataset: DatasetKind;
}) {
  const dispatch = useAppDispatch();
  const { loading, error, preview, result } = useAppSelector(
    s => s.dataHub.reset
  );
  const [preset, setPreset] = useState<"type_a" | "type_b">("type_a");
  const [typed, setTyped] = useState("");

  useEffect(() => {
    dispatch(clearDataHubResetState());
    setTyped("");
    setPreset("type_a");
  }, [dispatch, orgId, datasetKey]);

  const confirmed =
    preset === "type_a" ||
    typed.trim().toLowerCase() === orgName.trim().toLowerCase();

  const onClearResult = () => {
    dispatch(clearDataHubResetState());
    setTyped("");
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="m-0 text-base font-semibold text-gray-900">
          Org-wise data reset — {orgName}
        </h3>
        <p className="m-0 text-xs text-gray-500">
          Resets only the <strong>{DATASET_LABELS[dataset]}</strong> dataset.
          Choose a stage scope, preview the impact, then confirm delete.
        </p>
      </div>

      {error && <ErrorBanner text={error} />}

      {result && (
        <div className="space-y-3 rounded-md border border-green-200 bg-green-50 p-3">
          <p className="m-0 text-sm font-medium text-green-800">
            Deleted {Number(result.total_deleted || 0).toLocaleString()} rows
            {result.dataset_kind
              ? ` from ${DATASET_LABELS[coerceDataset(result.dataset_kind)]}`
              : ""}
            .
          </p>
          {(result.targets || []).length > 0 && (
            <div className="space-y-1">
              {(result.targets || []).map((t: any) => (
                <div
                  key={t.stage}
                  className="flex justify-between text-xs text-green-900"
                >
                  <span>{t.label}</span>
                  <span className="tabular-nums">
                    {Number(t.deleted ?? t.rows ?? 0).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
          <Button
            type="button"
            variant="outline-primary"
            onClick={onClearResult}
          >
            Run another reset
          </Button>
        </div>
      )}

      {!result && (
        <>
          <fieldset className="m-0 min-w-0 border-0 p-0">
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Reset scope (within {DATASET_LABELS[dataset]})
            </legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <label
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                  preset === "type_a"
                    ? "border-purple-400 bg-purple-50 ring-1 ring-purple-300"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="org-reset-preset"
                  value="type_a"
                  checked={preset === "type_a"}
                  className="mt-1 h-4 w-4 shrink-0 accent-purple-600"
                  onChange={() => {
                    setPreset("type_a");
                    dispatch(clearDataHubResetState());
                  }}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900">
                    Type A — Staging only
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500">
                    Clears staging rows for this dataset. Committed timeseries
                    and scores are left untouched.
                  </span>
                </span>
              </label>

              <label
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                  preset === "type_b"
                    ? "border-red-300 bg-red-50 ring-1 ring-red-200"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="org-reset-preset"
                  value="type_b"
                  checked={preset === "type_b"}
                  className="mt-1 h-4 w-4 shrink-0 accent-red-600"
                  onChange={() => {
                    setPreset("type_b");
                    dispatch(clearDataHubResetState());
                  }}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900">
                    Type B — Staging + committed data
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500">
                    Removes staging and committed timeseries / summaries /
                    benchmarks for this dataset only. Requires typing the
                    organization name to confirm.
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          {preview && (
            <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
              <p className="m-0 font-medium text-amber-900">
                Preview ({DATASET_LABELS[dataset]}) —{" "}
                {Number(
                  preview.total_rows ?? preview.total_deleted ?? 0
                ).toLocaleString()}{" "}
                rows would be deleted
              </p>
              {(preview.targets || []).map((t: any) => (
                <div key={t.stage} className="flex justify-between text-xs">
                  <span>
                    {t.label}
                    {t.skipped ? (
                      <span className="text-gray-500"> ({t.skipped})</span>
                    ) : null}
                  </span>
                  <span className="tabular-nums">
                    {Number(t.rows ?? t.deleted ?? 0).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}

          {preset === "type_b" && (
            <label className="block text-xs text-gray-600">
              Type organization name to confirm
              <input
                className="mt-1 w-full rounded border border-red-300 px-2 py-1.5 text-sm"
                value={typed}
                onChange={e => setTyped(e.target.value)}
                placeholder={orgName}
              />
            </label>
          )}

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline-primary"
              disabled={loading}
              loading={loading && !preview}
              onClick={() =>
                void dispatch(
                  previewDataHubReset({ organizationId: orgId, preset })
                )
              }
            >
              Preview
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={loading || !preview || !confirmed}
              loading={loading && !!preview}
              onClick={() =>
                void dispatch(
                  executeDataHubReset({
                    organizationId: orgId,
                    preset,
                    confirmOrgName: preset === "type_b" ? orgName : undefined,
                  })
                )
              }
            >
              Delete
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function ErrorBanner({ text }: { text: string }) {
  return (
    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
      {text}
    </div>
  );
}

function LoadingRow({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-sm text-gray-600">
      <Loader2 className="h-4 w-4 animate-spin" /> {text}
    </div>
  );
}

export default AdminMetricsConfigContainer;
